/** @jsxImportSource @emotion/react */
import { useRef, useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useEvent from 'react-use-event-hook';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { Button } from '@mui/material';
import { CellType } from '@grapecity/wijmo.grid';
import { InputDate } from '@grapecity/wijmo.input';
import { hasClass, addClass } from '@grapecity/wijmo';
import PropTypes from 'prop-types';
import CustomGrid from 'components/grids/CustomGrid';
import { tabs } from 'components/layouts/Tab';
import { SubTitleLayout, SubTitleGroup, ControlBtnGroup } from 'components/layouts/ContentLayout';
import CustomDialog from 'components/modals/common/CustomDialog';
import { IconButton } from 'components/buttons/IconSVG';
import { CommonUtil } from 'utils/CommonUtil';
import { SetupScheduleCondition, SetupTargetStepScheduleRequest } from 'models/setup/SetupSchedule';
import useSessionStore from 'stores/useSessionStore';
import { useMessageBar } from 'hooks/useMessageBar';
import { useCommonModal } from 'hooks/useCommonModal';
import {
  BoxApproveRequestApprove,
  BoxApproveRequestReject,
  findBoxApproveRequestHistory,
} from 'apis/setup/excp/box';
import { BoxAprvReq } from 'models/setup/excp/excp';
import { hasRole } from 'utils/SessionUtil';

type Props = {
  open: boolean;
  close: () => void;
  condition: {
    elmPrjId: string;
    excpId?: string;
  };
};

const SetupFabInRequestPopup = ({ open, close, ...props }: Props) => {
  const { t } = useTranslation();
  const { ensolYn } = useSessionStore();
  const [currentTabIndex, setCurrentTabIndex] = useState<number>(0);
  const [condition, setCondition] = useState<SetupScheduleCondition>(props?.condition);
  const tabMenus = useMemo(() => {
    return [
      { id: 'REQ', name: t('setup.label.도착/Fab-In 처리', '도착/Fab-In 처리') },
      { id: 'HIST', name: t('setup.label.이력 보기', '이력 보기') },
    ];
  }, []);

  return (
    <CustomDialog
      title={String(t('setup.label.Box 도착/Fab-In 승인', 'Box 도착/Fab-In 승인'))}
      size={'xl'}
      open={open}
      onClose={close}
    >
      <div css={tabs.wrap}>
        <Tabs
          value={currentTabIndex}
          onChange={(e, newValue) => setCurrentTabIndex(newValue)}
          css={tabs.tab}
        >
          {tabMenus.map((o, idx) => (
            <Tab
              key={o.id}
              label={o.name}
              id={`setup-approve-tab-${idx}`}
              aria-controls={`setup-approve-tabpanel-${idx}`}
            />
          ))}
        </Tabs>
      </div>
      <TabPanel value={currentTabIndex} index={currentTabIndex}>
        {currentTabIndex === 0 ? (
          <RequestGrid condition={condition} ensolYn={ensolYn} />
        ) : (
          <HistoryGrid condition={condition} ensolYn={ensolYn} />
        )}
      </TabPanel>
    </CustomDialog>
  );
};
TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function TabPanel(props: { [x: string]: any; children: any; value: any; index: any }) {
  const { children, value, index } = props;

  return (
    <div role="tabpanel" hidden={value !== index} id={`setup-approve-tabpanel-${index}`}>
      {value === index && <div>{children}</div>}
    </div>
  );
}

const HistoryGrid = ({ condition }: any) => {
  const { t } = useTranslation();
  const gridRef = useRef<any>();
  const [rowData, setRowData] = useState<BoxAprvReq[]>([]);
  const dateEditor = useRef(
    new InputDate(document.createElement('div'), {
      format: 'yyyy.MM.dd',
      isRequired: false,
    })
  );

  useEffect(() => {
    if (condition) {
      handleSearch(condition);
    }
  }, [condition]);

  const handleDownloadExcel = () => {
    CommonUtil.exportWijmoExcelGrid(
      gridRef.current,
      t('setup.label.셋업 실적 처리 목록', '셋업 실적 처리 목록'),
      t('setup.label.셋업 실적 처리 목록', '셋업 실적 처리 목록')
    );
  };

  const handleSearch = async (params) => {
    findBoxApproveRequestHistory({ ...params, aplCpltYn: 'Y' }).then((result) =>
      setRowData(result)
    );
  };

  const onInitialized = (grid) => {
    gridRef.current = grid;
  };

  const layoutDefinition = useMemo(() => {
    return [
      {
        binding: 'no',
        header: String(t('com.label.NO', 'NO')),
        width: 40,
        align: 'center',
        isReadOnly: true,
        cellTemplate: (grid) => grid.row._idx + 1,
      },
      {
        binding: 'aprReqTpCdNm',
        header: String(t('setup.label.유형', '유형')),
        minWidth: 80,
        width: 90,
        align: 'center',
        isReadOnly: true,
      },
      {
        binding: 'pakBoxNo',
        header: String(t('setup.label.Box No', 'Box No')),
        minWidth: 80,
        width: 130,
        align: 'center',
        isReadOnly: true,
      },
      {
        binding: 'pakItemNm',
        header: String(t('setup.label.품목 목록명', '품목 목록명')),
        minWidth: 80,
        width: '*',
        align: 'left',
        isReadOnly: true,
      },
      {
        binding: 'mcodeList',
        header: String(t('setup.label.M-Code List', 'M-Code List')),
        minWidth: 80,
        width: '*',
        align: 'center',
        isReadOnly: true,
      },
      {
        binding: 'excpNm',
        header: String(t('setup.label.협력업체명', '협력업체명')),
        minWidth: 80,
        width: 140,
        align: 'left',
        isReadOnly: true,
      },
      {
        binding: 'inptDtm',
        header: String(t('setup.label.처리일자', '처리일자')),
        minWidth: 80,
        width: 120,
        align: 'center',
        isReadOnly: true,
      },
      {
        binding: 'dataInsDtm',
        header: String(t('setup.label.요청일자', '요청일자')),
        minWidth: 80,
        width: 120,
        align: 'center',
        isReadOnly: true,
      },
      {
        binding: 'aprReqUserNm',
        header: String(t('setup.label.요청자', '요청자')),
        minWidth: 80,
        width: 100,
        align: 'center',
        isReadOnly: true,
      },
      {
        binding: 'aprProgStatCdNm',
        header: String(t('setup.label.상태', '상태')),
        minWidth: 80,
        width: 80,
        align: 'center',
        cssClass: 'WijmoTag',
        isReadOnly: true,
        cellTemplate: (ctx) => {
          if (ctx.item.aprProgStatCd === 'APV') {
            return `<span class="green">${ctx.value}</span>`;
          } else if (ctx.item.aprProgStatCd === 'REJ') {
            return `<span class="red">${ctx.value}</span>`;
          } else {
            return `<span class="grey">${ctx.value}</span>`;
          }
        },
      },
      {
        binding: 'aprAprvUserNm',
        header: String(t('setup.label.승인자', '승인자')),
        minWidth: 80,
        width: 100,
        align: 'center',
        isReadOnly: true,
      },
      {
        binding: 'rmk',
        header: String(t('setup.label.결재 Comment', '결재 Comment')),
        minWidth: 80,
        width: '*',
        align: 'left',
        isReadOnly: true,
      },
    ];
  }, []);

  return (
    <>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('setup.label.Box 도착/Fab-In 요청 이력', 'Box 도착/Fab-In 요청 이력')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{(rowData || []).length.toLocaleString()}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
        <ControlBtnGroup>
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={handleDownloadExcel}
            disableRipple
          >
            {t('com.button.다운로드', '다운로드')}
          </Button>
          <Button
            css={IconButton.button}
            className="refresh"
            onClick={() => handleSearch(condition)}
          >
            {t('com.button.새로고침', '새로고침')}
          </Button>
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid
        id={`SetupFabInRequestPopupHistoryGrid`}
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        height={443}
        isSelector={false}
        allowDragging={false}
        initialized={onInitialized}
      />
    </>
  );
};

const RequestGrid = ({ condition, ensolYn }: any) => {
  const { t } = useTranslation();
  const { openMessageBar } = useMessageBar();
  const { openCommonModal } = useCommonModal();
  const { userId, roleCodes } = useSessionStore();
  const gridRef = useRef<any>();
  const [rowData, setRowData] = useState<BoxAprvReq[]>([]);

  const isEmailFormat = () => {
    if (!userId) return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(userId);
  };

  useEffect(() => {
    if (condition) {
      handleSearch(condition);
    }
  }, [condition]);

  const handleDownloadExcel = () => {
    CommonUtil.exportWijmoExcelGrid(
      gridRef.current,
      t('setup.label.Box 도착/Fab-In 요청 목록', 'Box 도착/Fab-In 요청 목록'),
      t('setup.label.Box 도착/Fab-In 요청 목록', 'Box 도착/Fab-In 요청 목록')
    );
  };

  const handleSearch = (params) => {
    findBoxApproveRequestHistory({ ...params, aplCpltYn: 'N' }).then((result) =>
      setRowData(result)
    );
  };

  const layoutDefinition = useMemo(() => {
    return [
      {
        binding: 'no',
        header: String(t('com.label.NO', 'NO')),
        width: 40,
        align: 'center',
        isReadOnly: true,
        cellTemplate: (grid) => grid.row._idx + 1,
      },
      {
        binding: 'aprReqTpCdNm',
        header: String(t('setup.label.유형', '유형')),
        minWidth: 80,
        width: 90,
        align: 'center',
        isReadOnly: true,
      },
      {
        binding: 'pakBoxNo',
        header: String(t('setup.label.Box No', 'Box No')),
        minWidth: 80,
        width: 130,
        align: 'center',
        isReadOnly: true,
      },
      {
        binding: 'pakItemNm',
        header: String(t('setup.label.품목 목록명', '품목 목록명')),
        minWidth: 80,
        width: '*',
        align: 'left',
        isReadOnly: true,
      },
      {
        binding: 'mcodeList',
        header: String(t('setup.label.M-Code List', 'M-Code List')),
        minWidth: 80,
        width: '*',
        align: 'center',
        isReadOnly: true,
      },
      {
        binding: 'excpNm',
        header: String(t('setup.label.협력업체명', '협력업체명')),
        minWidth: 80,
        width: 140,
        align: 'left',
        isReadOnly: true,
      },
      {
        binding: 'inptDtm',
        header: String(t('setup.label.처리일자', '처리일자')),
        minWidth: 80,
        width: 100,
        align: 'center',
        isReadOnly: true,
      },
      {
        binding: 'dataInsDtm',
        header: String(t('setup.label.요청일자', '요청일자')),
        minWidth: 80,
        width: 100,
        align: 'center',
        isReadOnly: true,
      },
      {
        binding: 'aprProgStatCdNm',
        header: String(t('setup.label.상태/요청', '상태/요청')),
        minWidth: 80,
        width: 100,
        align: 'center',
        isReadOnly: true,
        visible: isEmailFormat(),
      },
      {
        binding: 'aprAprvUserNm',
        header: String(t('setup.label.승인자', '승인자')),
        minWidth: 80,
        width: 100,
        align: 'center',
        isReadOnly: true,
        visible: isEmailFormat(),
      },
      {
        binding: 'aprReqUserNm',
        header: String(t('setup.label.요청자', '요청자')),
        minWidth: 80,
        width: 100,
        align: 'center',
        isReadOnly: true,
        visible: (hasRole('FA_USER') || hasRole('FA_USER_COP')) && !isEmailFormat(),
      },
      {
        binding: 'aprProgStatCd',
        header: String(t('setup.label.승인', '승인')),
        minWidth: 80,
        width: 80,
        align: 'center',
        isReadOnly: true,
        visible: (hasRole('FA_USER') || hasRole('FA_USER_COP')) && !isEmailFormat(),
        cssClass: 'WijmoBasicButton noTooltip',
        cellTemplate: (grid) => {
          const item = grid.item;

          if (grid.value === 'INP') {
            return `
              <button class="approve" data-id="fabInBtn">
                ${t('com.label.승인', '승인')}
              </button>`;
          } else {
            return '';
          }
        },
      },
      {
        binding: 'aprProgStatCd',
        header: String(t('setup.label.반려', '반려')),
        minWidth: 80,
        width: 80,
        align: 'center',
        isReadOnly: true,
        visible: (hasRole('FA_USER') || hasRole('FA_USER_COP')) && !isEmailFormat(),
        cssClass: 'WijmoBasicButton noTooltip',
        cellTemplate: (grid) => {
          const item = grid.item;

          if (grid.value === 'INP') {
            return `
              <button class="reject" data-id="fabInBtn">
                ${t('com.label.반려', '반려')}
              </button>`;
          } else {
            return '';
          }
        },
      },
      {
        binding: 'rmk',
        header: String(t('setup.label.결재 Comment', '결재 Comment')),
        minWidth: 80,
        width: '*',
        align: 'left',
        isReadOnly: !((hasRole('FA_USER') || hasRole('FA_USER_COP')) && !isEmailFormat()),
      },
    ];
  }, [ensolYn]);

  const validateRequest = (items, isSingle = true) => {
    if ((items || []).length < 1) {
      openCommonModal({
        content: t('setup.label.선택된 항목이 없습니다.', '선택된 항목이 없습니다.'),
      });
      return false;
    }
    let valid = [] as (string | undefined)[];
    // 엔솔인 경우 승인/반려 처리
    if (ensolYn === 'Y') {
      // 반려인 경우 결재 Comment 필수값
      valid = (items || []).map((o, idx) => {
        if (!o.rmk) {
          return `${!isSingle ? `[${idx + 1}행]` : ''}${t(
            'setup.label.결재 Comment를 입력해 주세요.',
            '결재 Comment를 입력해 주세요.'
          )}`;
        }
      });
    }
    // 협력사인 경우 요청 처리
    else {
      // 실제완료일 확인
      valid = (items || [])
        .map((o, idx) => {
          if (!o.atulEndDt) {
            return `${!isSingle ? `[${idx + 1}행] ` : ''}${t(
              'setup.label.실제완료일을 입력해 주세요.',
              '실제완료일을 입력해 주세요.'
            )}`;
          }
        })
        .filter((element) => element !== undefined);
    }
    if (valid.length) {
      openCommonModal({
        content: valid.join('<br/>'),
      });
      return false;
    }
    return true;
  };

  const validateApproveReject = (
    items: SetupTargetStepScheduleRequest[],
    isReject = false,
    isSingle = true
  ) => {
    if ((items || []).length < 1) {
      openCommonModal({
        content: t('setup.label.선택된 항목이 없습니다.', '선택된 항목이 없습니다.'),
      });
      return false;
    }
    let valid = [] as (string | undefined)[];
    if (ensolYn === 'Y' && isReject) {
      // 반려인 경우 결재 Comment 필수값
      valid = (items || [])
        .map((o, idx) => {
          if (!o.rmk) {
            return `${!isSingle ? `[${idx + 1}행]` : ''}${t(
              'setup.label.결재 Comment를 입력해 주세요.',
              '결재 Comment를 입력해 주세요.'
            )}`;
          }
        })
        .filter((element) => element !== undefined);
    }
    if (valid.length) {
      openCommonModal({
        content: valid.join('<br/>'),
      });
      return false;
    }
    return true;
  };

  const onItemFormatter = useEvent((panel, row, col, cell) => {
    if (CellType.Cell === panel.cellType) {
      const binding = panel.columns[col]?.binding;
      const item = panel.rows[row]?.dataItem;
      if ('rmk' === binding) {
        cell.ariaReadOnly = item.aprProgStatCd !== 'INP' || ensolYn === 'N';
      }
    }
  });

  const onInitialized = (grid) => {
    gridRef.current = grid;
    grid.itemFormatter = onItemFormatter;

    // selector disabled 처리
    grid.formatItem.addHandler((s, e) => {
      /*if (e.panel.cellType === 2 && e.col === 1 && e.cell.querySelector('input').indeterminate) {
        e.cell.querySelector('input').checked = true;
      }*/
      const item = s.rows[e.row]?.dataItem;
      if (e.panel === s.rowHeaders) {
        if (e.cell.hasChildNodes()) {
          if (
            (item.aprProgStatCd !== 'REQ' && ensolYn === 'N') ||
            (item.aprProgStatCd !== 'INP' && ensolYn === 'Y')
          ) {
            // e.cell.removeChild(e.cell.firstChild);
            addClass(e.cell, 'wj-state-disabled');
          }
        }
      }
    });

    grid.hostElement.addEventListener('click', (e) => {
      if (grid.rows.length == 0) return;

      const ht = grid.hitTest(e);
      if (ht.row < 0 || ht.col < 0) return;

      const item = grid.rows[ht.row]?.dataItem;
      const binding = grid.columns[ht.col]?.binding;

      // 전체 선택 시
      if (hasClass(e.target, 'wj-column-selector-group') && e.target.checked) {
        grid.deferUpdate(() => {
          grid.rows.forEach((row) => {
            if (
              (row?.dataItem.aprProgStatCd !== 'REQ' && ensolYn === 'N') ||
              (row?.dataItem.aprProgStatCd !== 'INP' && ensolYn === 'Y')
            ) {
              row.isSelected = false;
            }
          });
        });
      }

      if (e.target instanceof HTMLButtonElement) {
        // 승인인 경우
        if (e.target.classList.contains('approve')) {
          if (!validateApproveReject([item])) {
            return;
          }
          approveResultComplete([item]);
        }
        // 반려인 경우
        else if (e.target.classList.contains('reject')) {
          if (!validateApproveReject([item], true)) {
            return;
          }
          rejectResultComplete([item]);
        }
      }
    });
  };

  /**
   * 일괄승인
   */
  const handleAllApprove = () => {
    const checked = gridRef.current?.rows.filter((r) => r.isSelected).map((o) => o.dataItem);
    if ((checked || []).length < 1) {
      openCommonModal({
        content: t(
          'setup.label.일괄 승인할 항목을 선택해 주세요.',
          '일괄 승인할 항목을 선택해 주세요.'
        ),
      });
      return;
    }
    if (!validateApproveReject(checked, false, false)) {
      return;
    }
    approveResultComplete(checked, true);
  };

  /**
   * Box 도착/Fab-In 승인 처리
   * @param items
   */
  const approveResultComplete = (items: any, isMulti = false) => {
    BoxApproveRequestApprove(items).then((res) => {
      if (res?.successOrNot === 'Y') {
        const data = res.data;
        if (isMulti) {
          // 일괄 처리인 경우
          openCommonModal({
            content: ` 
            <p class="font-bold">
              ${t('setup.label.도착/Fab-In 처리 결과', '도착/Fab-In 처리 결과')}
            </p>
            <br>
            <div class="flex"> 
              <span style="width: 50px;">${t('setup.label.성공', '성공')}</span>
              : <span class="ml-3">${data?.success || 0} 건</span>
            </div>
            <div class="flex">
              <span style="width: 50px;">${t('setup.label.실패', '실패')}</span>    
              : <span class="ml-3">${data?.fail || 0} 건</span>       
            </div>
        `,
          });
          handleSearch(condition);
        } else {
          // 단일 처리인 경우
          if (data.success === 1) {
            openMessageBar({
              type: 'confirm',
              content: t('setup.label.승인 처리되었습니다.', '승인 처리되었습니다.'),
            });
            setRowData((prev) => prev.filter((o) => !items.some((x) => x.aprReqId === o.aprReqId)));
          } else {
            openCommonModal({
              content:
                res.data && typeof res.data === 'string'
                  ? res.data
                  : t('setup.label.승인 처리에 실패했습니다.', '승인 처리에 실패했습니다.'),
            });
          }
        }
      } else {
        openCommonModal({
          content:
            res.data && typeof res.data === 'string'
              ? res.data
              : t('setup.label.처리 중 오류가 발생했습니다.', '처리 중 오류가 발생했습니다.'),
        });
      }
    });
  };

  /**
   * Box 도착/Fab-In 반려 처리
   * @param items
   */
  const rejectResultComplete = (items) => {
    BoxApproveRequestReject(items).then((res) => {
      if (res?.successOrNot === 'Y') {
        openMessageBar({
          type: 'confirm',
          content: t('setup.label.반려 처리되었습니다.', '반려 처리되었습니다.'),
        });
        setRowData((prev) => prev.filter((o) => !items.some((x) => x.aprReqId === o.aprReqId)));
      } else {
        openCommonModal({
          content:
            res.data && typeof res.data === 'string'
              ? res.data
              : t('setup.label.처리 중 오류가 발생했습니다.', '처리 중 오류가 발생했습니다.'),
        });
      }
    });
  };

  return (
    <>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('setup.label.Box 도착/Fab-In 요청 목록', 'Box 도착/Fab-In 요청 목록')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{(rowData || []).length.toLocaleString()}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
        <ControlBtnGroup>
          {ensolYn === 'Y' && (
            <Button
              css={IconButton.button}
              className="request"
              onClick={handleAllApprove}
              disableRipple
            >
              {String(t('setup.label.일괄 승인', '일괄 승인'))}
            </Button>
          )}
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={handleDownloadExcel}
            disableRipple
          >
            {t('com.button.다운로드', '다운로드')}
          </Button>
          <Button
            css={IconButton.button}
            className="refresh"
            onClick={() => handleSearch(condition)}
          >
            {t('com.button.새로고침', '새로고침')}
          </Button>
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid
        id={`SetupFabInRequestPopupRequestGrid`}
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        height={443}
        allowDragging={false}
        excludeFilter={['aprProgStatCd', 'approve', 'reject']}
        initialized={onInitialized}
      />
    </>
  );
};

export default SetupFabInRequestPopup;
